/**
 * Quick-Capture-Modal fuer ein neues persoenliches Todo.
 *
 * Wird sowohl vom Header-Plus-Button (PortalShell) als auch von der TodosPage
 * geoeffnet. Einzelne Quelle der Wahrheit fuer Datum-Pickling und Submit-Logik.
 */
import { useEffect, useRef, useState } from 'react';
import { X, Loader2, Calendar, UserPlus, Bell } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { UserPicker } from '@onvice/portal-ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;  // wird nach erfolgreichem Insert gerufen — Caller kann Liste reloaden
}

export default function TodoQuickCaptureModal({ open, onClose, onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [reminderAt, setReminderAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [assigneeLabel, setAssigneeLabel] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setDueAt('');
      setReminderAt('');
      setError('');
      setAssigneeId(null);
      setAssigneeLabel('');
      // Auto-Focus nach Modal-Mount.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (): Promise<void> => {
    const t = title.trim();
    if (!t) {
      setError('Titel fehlt');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/todos', {
        title: t,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        reminder_at: reminderAt ? new Date(reminderAt).toISOString() : null,
        assignee_user_id: assigneeId,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-24"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-card border border-default shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <h2 className="text-base font-semibold">Neues Todo</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Schliessen"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-4 space-y-4">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="Was ist zu tun?"
            className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
              <Calendar size={14} /> Faellig (optional)
            </label>
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
              <Bell size={14} /> Erinnerung (optional)
            </label>
            <input
              type="datetime-local"
              value={reminderAt}
              onChange={(e) => setReminderAt(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
              <UserPlus size={14} /> Zuweisen an (optional)
            </label>
            <UserPicker
              value={assigneeId}
              label={assigneeLabel}
              onChange={(id, name) => { setAssigneeId(id); setAssigneeLabel(name); }}
            />
          </div>
          {error && <div className="text-sm text-danger">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-default">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Anlegen
          </button>
        </div>
      </div>
    </div>
  );
}
